/**
 * fieldConditions.ts
 *
 * Manages conditional visibility logic for form fields.
 * Evaluates field conditions based on form values and dependencies.
 *
 * Condition System:
 * - Field dependencies
 * - Value comparison
 * - Multiple condition handling
 * - Dynamic evaluation
 * - Complex condition chains
 *
 * Supported Operations:
 * - Equals/Not Equals
 * - Contains/Not Contains
 * - Greater Than/Less Than
 * - AND/OR logic
 *
 * Features:
 * - Real-time evaluation
 * - Multiple field dependencies
 * - Type-safe comparisons
 * - Default visibility
 * - Error handling
 *
 * Integration:
 * - Form rendering
 * - Field visibility
 * - Dynamic form flow
 * - Data validation
 */

import { Field, FieldCondition } from "../types/types";

/**
 * Evaluates field conditions to determine visibility
 * @param field The field to evaluate conditions for
 * @param formValues Current form values
 * @param allFields All fields in the form
 * @returns boolean indicating whether the field should be visible
 */
export const evaluateFieldConditions = (
  field: Field,
  formValues: Record<string, any>,
  allFields: Field[],
): boolean => {
  const conditions = field.visibilityConditions || field.conditions;
  if (!conditions || conditions.length === 0) {
    return true;
  }

  const checkCondition = (condition: FieldCondition): boolean => {
    const sourceField = allFields.find((f) => f.id === condition.fieldId);
    if (!sourceField) return false;

    const fieldValue = formValues[condition.fieldId];

    switch (condition.operator) {
      case "equals":
        return fieldValue === condition.value;
      case "notEquals":
        return fieldValue !== condition.value;
      case "contains":
        return String(fieldValue).includes(String(condition.value));
      case "notContains":
        return !String(fieldValue).includes(String(condition.value));
      case "greater":
        return Number(fieldValue) > Number(condition.value);
      case "less":
        return Number(fieldValue) < Number(condition.value);
      case "greaterOrEqual":
        return Number(fieldValue) >= Number(condition.value);
      case "lessOrEqual":
        return Number(fieldValue) <= Number(condition.value);
      case "isEmpty":
        return !fieldValue || fieldValue === "" || fieldValue === null || fieldValue === undefined;
      case "isNotEmpty":
        return fieldValue && fieldValue !== "" && fieldValue !== null && fieldValue !== undefined;
      case "before":
        // Date comparison: fieldValue is before condition.value
        try {
          const fieldDate = new Date(fieldValue);
          const compareDate = new Date(condition.value as string);
          return fieldDate < compareDate;
        } catch {
          return false;
        }
      case "after":
        // Date comparison: fieldValue is after condition.value
        try {
          const fieldDate = new Date(fieldValue);
          const compareDate = new Date(condition.value as string);
          return fieldDate > compareDate;
        } catch {
          return false;
        }
      case "between":
        // Date/number is between two values
        // condition.value should be an array [min, max]
        try {
          if (Array.isArray(condition.value) && condition.value.length === 2) {
            const numValue = Number(fieldValue);
            const min = Number(condition.value[0]);
            const max = Number(condition.value[1]);
            return numValue >= min && numValue <= max;
          }
          return false;
        } catch {
          return false;
        }
      default:
        return false;
    }
  };

  // If showIf is not specified, default to requiring all conditions to be met
  return field.showIf === "any"
    ? conditions.some(checkCondition)
    : conditions.every(checkCondition);
};
